import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import {
  NOTES_CATEGORY_FILTER_OPTIONS,
  sanitizeNotesCategoryFilter,
} from "../constants/notesPreferences";
import "./MemoryGamePreferenceDropdown.css";
import "./NotesCategoryFilterDropdown.css";

export default function NotesCategoryFilterDropdown({
  value = "all",
  onChange = () => {},
}) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const selected = sanitizeNotesCategoryFilter(value);

  const selectedOption = useMemo(
    () =>
      NOTES_CATEGORY_FILTER_OPTIONS.find((option) => option.value === selected) ||
      NOTES_CATEGORY_FILTER_OPTIONS[0],
    [selected]
  );

  useEffect(() => {
    if (!dropdownOpen) return undefined;

    const handlePointerDown = (event) => {
      if (dropdownRef.current?.contains(event.target)) return;
      setDropdownOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setDropdownOpen(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [dropdownOpen]);

  const handleSelect = (next) => {
    onChange(sanitizeNotesCategoryFilter(next));
    setDropdownOpen(false);
  };

  return (
    <div
      className={`memory-game-pref-dropdown notes-category-filter-dropdown${
        dropdownOpen ? " memory-game-pref-dropdown--open" : ""
      }`}
      ref={dropdownRef}
    >
      <button
        type="button"
        className="memory-game-pref-dropdown__trigger"
        aria-haspopup="listbox"
        aria-expanded={dropdownOpen}
        onClick={() => setDropdownOpen((open) => !open)}
      >
        <span className="memory-game-pref-dropdown__label">{selectedOption.label}</span>
        <ChevronDown
          size={16}
          strokeWidth={2}
          className="memory-game-pref-dropdown__chevron"
          aria-hidden="true"
        />
      </button>

      {dropdownOpen && (
        <ul className="memory-game-pref-dropdown__menu" role="listbox">
          {NOTES_CATEGORY_FILTER_OPTIONS.map((option) => (
            <li key={option.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={option.value === selected}
                className={`memory-game-pref-dropdown__option${
                  option.value === selected
                    ? " memory-game-pref-dropdown__option--selected"
                    : ""
                }`}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
